<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import { formatApiError } from '@yuksalish/api-client'
import { useHrApi } from '../composables/useHrApi'
import type { Employee } from '../types/hr'

const props = withDefaults(
  defineProps<{
    open: boolean
    title?: string
    eyebrow?: string
    searchLabel?: string
    searchPlaceholder?: string
    emptyMessage?: string
    loadingMessage?: string
    excludeEmployeeId?: number | null
  }>(),
  {
    title: '',
    eyebrow: '',
    searchLabel: '',
    searchPlaceholder: '',
    emptyMessage: '',
    loadingMessage: '',
    excludeEmployeeId: null,
  },
)

const emit = defineEmits<{
  close: []
  select: [employee: Employee]
}>()

const { t } = useI18n()
const { listEmployees } = useHrApi()

const searchValue = ref('')
const items = ref<Employee[]>([])
const loading = ref(false)
const error = ref('')
const page = ref(1)
const totalPages = ref(1)

const resolved = {
  title: () => props.title || t('hr.picker.employeeTitle'),
  eyebrow: () => props.eyebrow || t('hr.picker.employeeEyebrow'),
  searchLabel: () => props.searchLabel || t('hr.picker.employeeSearch'),
  searchPlaceholder: () => props.searchPlaceholder || t('hr.picker.employeePlaceholder'),
  emptyMessage: () => props.emptyMessage || t('hr.picker.employeeEmpty'),
  loadingMessage: () => props.loadingMessage || t('hr.picker.employeeLoading'),
}

function employeeName(employee: Employee): string {
  const name = `${employee.last_name ?? ''} ${employee.first_name ?? ''}`.trim()
  return name || employee.employee_code
}

function toOptions(list: Employee[]): SmartPickerOption[] {
  return list
    .filter((employee) => employee.id !== props.excludeEmployeeId)
    .map((employee) => ({
      id: employee.id,
      title: employeeName(employee),
      subtitle: employee.employee_code,
      meta: employee.job_title || employee.department_name || '',
    }))
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listEmployees({
      search: searchValue.value.trim() || undefined,
      is_active: 1,
      page: page.value,
      per_page: 20,
    })
    items.value = result.items
    totalPages.value = Math.max(1, result.meta.total_pages)
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

function openModal() {
  searchValue.value = ''
  page.value = 1
  items.value = []
  void load()
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) openModal()
  },
)

watch(searchValue, () => {
  page.value = 1
  void load()
})

onMounted(() => {
  if (props.open) openModal()
})
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="resolved.title()"
    :eyebrow="resolved.eyebrow()"
    :search-label="resolved.searchLabel()"
    :search-placeholder="resolved.searchPlaceholder()"
    :search-value="searchValue"
    :loading="loading"
    :error-message="error"
    :empty-message="resolved.emptyMessage()"
    :items="toOptions(items)"
    :page="page"
    :total-pages="totalPages"
    :page-label="t('hr.picker.page', { page, total: Math.max(totalPages, 1) })"
    :close-aria-label="t('hr.picker.close')"
    :retry-label="t('hr.picker.retry')"
    :previous-label="t('hr.picker.previous')"
    :next-label="t('hr.picker.next')"
    :cancel-label="t('hr.picker.cancel')"
    @close="emit('close')"
    @retry="load"
    @previous="page = Math.max(1, page - 1); load()"
    @next="page = Math.min(totalPages, page + 1); load()"
    @select="(option: SmartPickerOption) => {
      const match = items.find((employee) => String(employee.id) === String(option.id))
      if (match) emit('select', match)
    }"
    @update:search-value="(value: string) => (searchValue = value)"
  >
    <template #loading>{{ resolved.loadingMessage() }}</template>
  </SmartPickerModal>
</template>
